import { useMemo, useState } from "react";

import FormComp from "../../Componentes/FormComp";
import Overlay from "../../Componentes/Overlay";
import Formulario from "../../Componentes/Utils/Formulario";
import Modal from "../../Componentes/Utils/Modal";
import { useModal } from "../../contexts/ModalContext";
import useApi from "../../hooks/useApi";

/**
 * Modal de Apontamento de Folha.
 *
 * O usuário escolhe um ou mais centros de custo (somente os que ele tem
 * permissão — a lista vem do componente pai, derivada da API
 * /Painel/API/FolhaPag/Valores que já filtra por user_access) e o mês
 * de referência. Ao confirmar:
 *   1. Filtra o array completo `dados` pelos CCs selecionados;
 *   2. Envia esse subconjunto + CCs + mês para o backend;
 *   3. O backend monta o XLSX de apontamento e dispara o e-mail com
 *      o anexo para matheus.oliveira@palmont.com.br.
 *
 * Observação: o filtro por CC é feito aqui (mesmo padrão do botão de
 * "RELATÓRIO DE REFERÊNCIA"), mas o backend também valida defensivamente
 * cada linha contra a tabela user_access.
 */
const ModalApontamentoFolha = ({ centrosCustoOpts = [], dados = [] }) => {

    const { closeModal } = useModal();
    const api = useApi();

    const [enviando, setEnviando] = useState(false);
    const [centrosSelecionados, setCentrosSelecionados] = useState([]);
    const [mes, setMes] = useState("");

    /* centrosCustoOpts é um array de strings ("01 - OBRA X").
       O FormComp type="multiselect" usa react-select, que precisa de
       { value, label }. */
    const ccOptions = useMemo(
        () => centrosCustoOpts.map((cc) => ({ value: cc, label: cc })),
        [centrosCustoOpts]
    );

    /* Limita o seletor de mês ao mês corrente — não faz sentido apontar
       folha de um mês futuro. */
    const mesAtual = useMemo(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    }, []);

    const handleSubmit = async (e) => {

        e.preventDefault();

        if (!centrosSelecionados || centrosSelecionados.length === 0) {
            alert("Selecione ao menos um centro de custo.");
            return;
        }

        if (!mes) {
            alert("Selecione o mês de referência.");
            return;
        }

        if (mes > mesAtual) {
            alert("Não é possível apontar folha de um mês futuro.");
            return;
        }

        const ccsSelecionados = centrosSelecionados.map((o) => o.value);
        const ccsSet = new Set(ccsSelecionados);

        /* Filtra o array completo (não filtrado pela tela) pelos CCs e
           pelo mês escolhidos no modal. r.data vem como string YYYY-MM-DD,
           então basta comparar o prefixo de 7 chars com o mes (YYYY-MM). */
        const dadosFiltrados = (dados || []).filter((r) => {
            if (!r || !ccsSet.has(r.centro_custo)) return false;
            const dataStr = String(r.data || "");
            return dataStr.slice(0, 7) === mes;
        });

        if (dadosFiltrados.length === 0) {
            alert("Nenhum lançamento encontrado para os centros de custo e mês selecionados.");
            return;
        }

        try {

            setEnviando(true);

            const payload = {
                centros_custo: ccsSelecionados,
                mes,
                dados: dadosFiltrados,
            };

            const { data } = await api.post(
                "/Painel/API/FolhaPag/EnviarApontamento/",
                payload
            );

            if (data?.status === "SUCCESS") {
                alert("Apontamento enviado com sucesso!");
                closeModal();
            } else {
                alert(data?.message || "Não foi possível enviar o apontamento.");
            }

        } catch (err) {

            console.error(err);
            const msg = err?.response?.data?.message || "Erro ao enviar apontamento.";
            alert(msg);

        } finally {

            setEnviando(false);

        }

    };

    return (
        <Overlay state={ true } >
            <Modal header close={ closeModal } >
                <Formulario onSubmit={ handleSubmit } >
                    <FormComp
                        title="CENTRO(S) DE CUSTO"
                        type="multiselect"
                        placeholder="SELECIONE UM OU MAIS CENTROS DE CUSTO"
                        options={ ccOptions }
                        valor={ centrosSelecionados }
                        changeFunc={ (selected) => setCentrosSelecionados(selected || []) }
                        required
                    />
                    <FormComp
                        title="MÊS DE REFERÊNCIA"
                        type="month"
                        valor={ mes }
                        name="mes"
                        changeFunc={ (e) => setMes(e.target.value) }
                        required
                    />
                    <FormComp
                        type="submit"
                        valor={ enviando ? "ENVIANDO..." : "ENVIAR APONTAMENTO" }
                        disabled={ enviando }
                    />
                </Formulario>
            </Modal>
        </Overlay>
    );

};

export default ModalApontamentoFolha;
